import React from 'react';
import { CaretRight, CheckCircle, XCircle, Warning, MinusCircle } from '@phosphor-icons/react';
import { Text, Tooltip, TooltipTrigger } from '@chatsdet/ui';
import { motion, AnimatePresence } from 'framer-motion';
import type { WorkflowAssertionResult, WorkflowAssertionSummary } from '@chatsdet/types';
import type { OpenWorkflowIssueSummary } from '../../flow-list/types';
import { LinkAssertionIssueDialog } from './LinkAssertionIssueDialog';
import { issueHref } from '../../../../utils/issueLinks';

/**
 * Context that lets a failing assertion row offer "Link issue" (file a new
 * issue, or attach to one already open for this workflow) — see
 * `LinkAssertionIssueDialog`. Omit entirely on surfaces with no run/property
 * context (e.g. the per-turn assertions rendered from a chat transcript).
 */
export interface AssertionIssueLinkContext {
  workflowId: string;
  runId: string;
  /** Required to file a *new* issue; attaching to an already-open one doesn't need it. */
  propertyId: string | null;
  openIssues: OpenWorkflowIssueSummary[];
  /** Best-effort URL for the run's target environment. */
  primaryUrl?: string | null;
}

// ── Shared assertion counting ────────────────────────────────────────

/** Not-checked = configured on the node but never evaluated (step broke first). */
const isNotChecked = (r: WorkflowAssertionResult) => r.checked === false;

function countAssertionResults(results: WorkflowAssertionResult[]) {
  let fatalCount = 0;
  let failCount = 0;
  let warnCount = 0;
  let passCount = 0;
  let notCheckedCount = 0;
  for (const r of results) {
    if (isNotChecked(r)) notCheckedCount++;
    else if (r.passed) passCount++;
    else if (r.severity === 'fatal') fatalCount++;
    else if (r.severity === 'fail') failCount++;
    else warnCount++;
  }
  return { fatalCount, failCount, warnCount, passCount, notCheckedCount };
}

// ── Assertion Result Row ──────────────────────────────────────────────

function formatEvaluatedAt(iso: string | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString();
}

function AssertionDetailTooltip({ result }: { result: WorkflowAssertionResult }) {
  const verdict: { label: string; className: string } = isNotChecked(result)
    ? { label: 'NOT CHECKED', className: 'text-white/50' }
    : result.passed
      ? { label: 'PASSED', className: 'text-emerald-300' }
      : result.severity === 'warn'
        ? { label: 'WARN', className: 'text-amber-300' }
        : result.severity === 'fatal'
          ? { label: 'FATAL', className: 'text-red-400 font-bold' }
          : { label: 'FAILED', className: 'text-red-300' };
  const evaluatedAt = formatEvaluatedAt(result.evaluatedAt);
  return (
    <div className="space-y-1.5 max-w-sm pointer-events-none">
      <div className="flex items-center gap-2">
        <span className={`text-[10px] font-semibold tracking-wide ${verdict.className}`}>
          {verdict.label}
        </span>
        <span className="text-[10px] uppercase tracking-wide text-white/60">
          {result.severity} · {result.source}
          {result.assertionType ? ` · ${result.assertionType}` : ''}
        </span>
      </div>
      {result.label && (
        <div className="text-xs font-medium whitespace-pre-wrap break-words">{result.label}</div>
      )}
      {result.condition && result.condition !== result.label && (
        <div
          className={
            result.label
              ? 'text-[11px] font-mono text-white/70 whitespace-pre-wrap break-words'
              : 'text-xs whitespace-pre-wrap break-words'
          }
        >
          {result.condition}
        </div>
      )}
      {result.reason && (
        <div className="text-[11px] text-white/80 whitespace-pre-wrap break-words border-t border-white/10 pt-1">
          {result.reason}
        </div>
      )}
      {evaluatedAt && <div className="text-[10px] text-white/50">{evaluatedAt}</div>}
    </div>
  );
}

/** True for a blocking assertion failure eligible for issue-linking (not a warn, not skipped). */
const isLinkableFailure = (r: WorkflowAssertionResult) =>
  !isNotChecked(r) && !r.passed && (r.severity === 'fail' || r.severity === 'fatal');

/**
 * Chip (already linked) or "Link issue" action for one failing assertion row.
 * The workflow's current open-issues list is authoritative: `result.issueId`
 * was stamped from that same list at evaluation time, so a still-open link is
 * found by `key`, and an issue closed since shows "Link issue" again.
 *
 * The dialog itself is hoisted to `StepAssertions` (one instance, keyed by
 * the active assertion) rather than mounted per row — `onLinkIssue` just
 * requests it open for this result.
 */
function AssertionIssueAction({
  result,
  context,
  onLinkIssue,
}: {
  result: WorkflowAssertionResult;
  context: AssertionIssueLinkContext;
  onLinkIssue: (result: WorkflowAssertionResult) => void;
}) {
  const linkedIssue =
    context.openIssues.find((issue) => issue.subjectAssertionKey === result.key) ?? null;

  if (linkedIssue) {
    return (
      <a
        href={issueHref(linkedIssue.id)}
        onClick={(e) => e.stopPropagation()}
        className="inline-flex items-center gap-1 text-[10px] font-medium text-red-700 dark:text-red-300 hover:underline shrink-0"
      >
        Issue {linkedIssue.shortcode}
      </a>
    );
  }

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onLinkIssue(result);
      }}
      className="text-[10px] font-medium text-muted-foreground underline underline-offset-2 hover:text-foreground shrink-0"
    >
      Link issue
    </button>
  );
}

function AssertionResultRow({
  result,
  issueLinkContext,
  onLinkIssue,
}: {
  result: WorkflowAssertionResult;
  issueLinkContext?: AssertionIssueLinkContext;
  onLinkIssue?: (result: WorkflowAssertionResult) => void;
}) {
  const icon = isNotChecked(result) ? (
    <MinusCircle size={14} weight="fill" className="text-muted-foreground/50 shrink-0" />
  ) : result.passed ? (
    <CheckCircle size={14} weight="fill" className="text-emerald-500 shrink-0" />
  ) : result.severity === 'warn' ? (
    <Warning size={14} weight="fill" className="text-amber-500 shrink-0" />
  ) : result.severity === 'fatal' ? (
    <XCircle size={14} weight="fill" className="text-red-700 dark:text-red-300 shrink-0" />
  ) : (
    <XCircle size={14} weight="fill" className="text-red-500 shrink-0" />
  );

  const showIssueAction = issueLinkContext && onLinkIssue && isLinkableFailure(result);

  return (
    <TooltipTrigger delay={150} placement="right">
      <div className="flex items-start gap-2 py-1.5 px-1 cursor-default">
        {icon}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium truncate">{result.label ?? result.condition}</span>
            {result.assertionType && (
              <Text size="xs" className="text-muted-foreground shrink-0">
                {result.assertionType}
              </Text>
            )}
            <Text size="xs" className="text-muted-foreground/60 shrink-0">
              {result.source}
            </Text>
            {showIssueAction && (
              <AssertionIssueAction result={result} context={issueLinkContext} onLinkIssue={onLinkIssue} />
            )}
          </div>
          {!result.passed && result.reason && (
            <span className="text-xs text-muted-foreground mt-0.5 block overflow-hidden line-clamp-2">
              {result.reason}
            </span>
          )}
        </div>
      </div>
      <Tooltip>
        <AssertionDetailTooltip result={result} />
      </Tooltip>
    </TooltipTrigger>
  );
}

// ── Expandable Assertions List (for RunDetailsPanel step subsections) ─

type StepAssertionsProps = {
  results: WorkflowAssertionResult[];
  defaultExpanded?: boolean;
  /** Enables the "Link issue" action / linked-issue chip on failing rows. */
  issueLinkContext?: AssertionIssueLinkContext;
};

export function StepAssertions({
  results,
  defaultExpanded = false,
  issueLinkContext,
}: StepAssertionsProps) {
  const [expanded, setExpanded] = React.useState(defaultExpanded);
  // Single hoisted dialog instance for whichever row's "Link issue" was
  // clicked, instead of one dialog mounted per failing row.
  const [activeAssertion, setActiveAssertion] = React.useState<WorkflowAssertionResult | null>(
    null
  );

  if (results.length === 0) return null;

  const { fatalCount, failCount, warnCount, passCount, notCheckedCount } =
    countAssertionResults(results);

  return (
    <div className="ml-4 mt-1.5">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors py-1 px-1.5 -ml-1.5 rounded-md hover:bg-muted/50 group"
      >
        <motion.div animate={{ rotate: expanded ? 90 : 0 }} transition={{ duration: 0.15 }}>
          <CaretRight size={11} />
        </motion.div>
        <Text size="xs">
          {results.length} assertion{results.length !== 1 ? 's' : ''}
        </Text>
        {fatalCount > 0 && (
          <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-red-700 dark:text-red-300">
            <XCircle size={10} weight="fill" /> {fatalCount}
          </span>
        )}
        {failCount > 0 && (
          <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-red-600 dark:text-red-400">
            <XCircle size={10} weight="fill" /> {failCount}
          </span>
        )}
        {warnCount > 0 && (
          <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-amber-600 dark:text-amber-400">
            <Warning size={10} weight="fill" /> {warnCount}
          </span>
        )}
        {notCheckedCount > 0 && (
          <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-muted-foreground/70">
            <MinusCircle size={10} weight="fill" /> {notCheckedCount}
          </span>
        )}
        {passCount > 0 && fatalCount === 0 && failCount === 0 && warnCount === 0 && notCheckedCount === 0 && (
          <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-emerald-600 dark:text-emerald-400">
            <CheckCircle size={10} weight="fill" /> {passCount}
          </span>
        )}
      </button>
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="mt-1 ml-1 space-y-0 border-l-2 border-border/40 pl-2">
              {results.map((r, idx) => (
                <AssertionResultRow
                  key={`${r.key}-${idx}`}
                  result={r}
                  issueLinkContext={issueLinkContext}
                  onLinkIssue={issueLinkContext ? setActiveAssertion : undefined}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {issueLinkContext && activeAssertion && (
        <LinkAssertionIssueDialog
          key={activeAssertion.key}
          isOpen
          onOpenChange={(open) => {
            if (!open) setActiveAssertion(null);
          }}
          workflowId={issueLinkContext.workflowId}
          runId={issueLinkContext.runId}
          propertyId={issueLinkContext.propertyId}
          assertionKey={activeAssertion.key}
          assertionLabel={activeAssertion.label ?? activeAssertion.condition}
          openIssues={issueLinkContext.openIssues}
          primaryUrl={issueLinkContext.primaryUrl}
        />
      )}
    </div>
  );
}

// ── Assertion Summary Card ────────────────────────────────────────────

type AssertionSummaryCardProps = {
  summary: WorkflowAssertionSummary;
  results?: WorkflowAssertionResult[];
};

export function AssertionSummaryCard({ summary, results }: AssertionSummaryCardProps) {
  const [expanded, setExpanded] = React.useState(false);

  return (
    <div className="rounded-lg border border-border/60 bg-muted/30 p-3 space-y-2">
      <button
        type="button"
        onClick={() => results?.length ? setExpanded(!expanded) : undefined}
        className="flex items-center gap-2 w-full text-left"
      >
        <Text size="sm" className="font-medium">Assertions</Text>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
            <CheckCircle size={12} weight="fill" /> {summary.passed}
          </span>
          {summary.warnings > 0 && (
            <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
              <Warning size={12} weight="fill" /> {summary.warnings}
            </span>
          )}
          {summary.failures > 0 && (
            <span className="inline-flex items-center gap-1 text-xs text-red-600 dark:text-red-400">
              <XCircle size={12} weight="fill" /> {summary.failures}
            </span>
          )}
          {(summary.notChecked ?? 0) > 0 && (
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground/70">
              <MinusCircle size={12} weight="fill" /> {summary.notChecked} not checked
            </span>
          )}
          <Text size="xs" className="text-muted-foreground">
            of {summary.total}
          </Text>
        </div>
        {results?.length ? (
          <motion.div
            animate={{ rotate: expanded ? 90 : 0 }}
            transition={{ duration: 0.15 }}
            className="ml-auto text-muted-foreground"
          >
            <CaretRight size={12} />
          </motion.div>
        ) : null}
      </button>
      {summary.hasFailSeverityFailures && (
        <Text size="xs" className="text-muted-foreground">
          Workflow completed but failed due to assertion failures.
          {summary.issueCovered ? ' All failures have open issues.' : ''}
        </Text>
      )}
      {/* Show failed assertion conditions inline (no expand needed) */}
      {results && (() => {
        const failed = results.filter(
          (r) => !isNotChecked(r) && !r.passed && (r.severity === 'fail' || r.severity === 'fatal')
        );
        if (failed.length === 0) return null;
        const shown = failed.slice(0, 3);
        const remaining = failed.length - shown.length;
        return (
          <div className="space-y-1">
            {shown.map((r, idx) => (
              <div key={`fail-${r.key}-${idx}`} className="flex items-start gap-1.5">
                <XCircle
                  size={12}
                  weight="fill"
                  className={
                    r.severity === 'fatal'
                      ? 'text-red-700 dark:text-red-300 shrink-0 mt-0.5'
                      : 'text-red-500 shrink-0 mt-0.5'
                  }
                />
                <Text
                  size="xs"
                  className={
                    r.severity === 'fatal'
                      ? 'text-red-700 dark:text-red-300 line-clamp-1'
                      : 'text-red-600 dark:text-red-400 line-clamp-1'
                  }
                >
                  {r.label ?? r.condition}
                  {r.severity === 'fatal' ? ' — stopped the test' : ''}
                </Text>
              </div>
            ))}
            {remaining > 0 && (
              <Text size="xs" className="text-muted-foreground ml-5">
                +{remaining} more
              </Text>
            )}
          </div>
        );
      })()}
      <AnimatePresence>
        {expanded && results?.length && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="border-t border-border/40 pt-2 space-y-0">
              {results.map((r, idx) => (
                <AssertionResultRow key={`${r.key}-${idx}`} result={r} />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
